import * as THREE from 'three';
import gsap from 'gsap';

const INK_FRAG = /* glsl */ `
varying vec2 vUv;
uniform float uProgress;
uniform vec3 uColor;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * noise(p); p *= 2.15; a *= 0.5; }
  return v;
}

void main() {
  float d = distance(vUv, vec2(0.5));
  float n = fbm(vUv * 7.0) * 0.45;
  // ink blot grows from the center with a noisy organic edge
  float hi = uProgress * 1.4;
  float alpha = 1.0 - smoothstep(hi - 0.25, hi, d + n);
  gl_FragColor = vec4(uColor, alpha);
}
`;

const INK_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;


export class Transitions {
  private inkScene = new THREE.Scene();
  private inkCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private material: THREE.ShaderMaterial;

  constructor(_camera: THREE.PerspectiveCamera) {
    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthTest: false,
      uniforms: {
        uProgress: { value: 0 },
        uColor: { value: new THREE.Color('#0a0a0c') },
      },
      vertexShader: INK_VERT,
      fragmentShader: INK_FRAG,
    });
    this.inkScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material));
  }

  setInkColor(hex: string): void {
    (this.material.uniforms.uColor.value as THREE.Color).set(hex);
  }

  /** True while the ink overlay is visible/animating (cover, dive, dissolve).
   *  Keeps the render loop awake through transitions. */
  get active(): boolean {
    return (this.material.uniforms.uProgress.value as number) > 0.001;
  }

  /** Instantly flood the screen with ink (intro start state). */
  cover(): void {
    this.material.uniforms.uProgress.value = 1;
  }

  /** Dissolve the ink cover. duration 0 → instant (reduced motion). */
  async dissolve(duration = 0.9): Promise<void> {
    if (duration <= 0) {
      this.material.uniforms.uProgress.value = 0;
      return;
    }
    await gsap.to(this.material.uniforms.uProgress, {
      value: 0, duration, ease: 'power1.out',
    });
  }

  /** Composite the ink quad over the frame. Call after the main render. */
  render(renderer: THREE.WebGLRenderer): void {
    if ((this.material.uniforms.uProgress.value as number) <= 0.001) return;
    renderer.autoClear = false;
    renderer.render(this.inkScene, this.inkCamera);
    renderer.autoClear = true;
  }

  /** Ink-bleed in, synced with the camera dive (Controls.diveTo). */
  async diveIn(): Promise<void> {
    await gsap.to(this.material.uniforms.uProgress, { value: 1, duration: 0.7, ease: 'power1.in' });
  }

  /** Ink-bleed out as the camera returns. */
  async diveOut(): Promise<void> {
    await gsap.to(this.material.uniforms.uProgress, { value: 0, duration: 0.7, ease: 'power1.out' });
  }
}
